'use client'

import { Download, Share } from 'lucide-react'
import { useEffect } from 'react'
import { toast } from 'sonner'

import { usePwaInstall } from '../hooks/use-pwa-install'

const DEFAULT_TOAST_ID = 'pwa-install'

/** How to install on iPhone and iPad (Safari has no install prompt). Shared with the bell's push control. */
export const IOS_ADD_TO_HOME_SCREEN_STEPS = 'Tap the Share icon, then "Add to Home Screen".'

export interface InstallPromptProps {
  /** App display name shown in the toast title (e.g. "Ordo Tasks", "Ask Ordo"). */
  appName: string
  /** Optional toast id — override only if mounting more than one in the same tree. */
  toastId?: string
  /** Optional localStorage key for the dismissed state — defaults to a shared key. */
  dismissedKey?: string
}

/**
 * Renders the bottom-right "Install <appName>" toast when the browser
 * exposes the install prompt (Chrome `beforeinstallprompt` or iOS Safari's
 * Add-to-Home-Screen flow). Returns `null`; the toast is dispatched via
 * the Sonner `<Toaster />` mounted elsewhere in the app shell.
 *
 * Mount once near the root of the layout, e.g. next to your `<Toaster />`.
 * The companion side-effect import `@ordo/ui/pwa-install-capture` should
 * be the first import in `main.tsx` so Chrome's event isn't lost during
 * initial HTML parse.
 */
export function InstallPrompt({ appName, toastId = DEFAULT_TOAST_ID, dismissedKey }: InstallPromptProps) {
  const { installable, iosA2HS, triggerInstall, dismiss } = usePwaInstall({ dismissedKey })

  useEffect(() => {
    if (installable) {
      toast(`Install ${appName}`, {
        id: toastId,
        description: 'For faster access and offline use.',
        icon: <Download className="h-4 w-4" />,
        duration: Infinity,
        position: 'bottom-right',
        action: {
          label: 'Install',
          onClick: async () => {
            const { outcome } = await triggerInstall()
            if (outcome === 'accepted') {
              toast.success(`${appName} installed`)
            }
          },
        },
        cancel: {
          label: 'Dismiss',
          onClick: () => dismiss(),
        },
      })
      return
    }
    if (iosA2HS) {
      toast(`Install ${appName}`, {
        id: toastId,
        description: IOS_ADD_TO_HOME_SCREEN_STEPS,
        icon: <Share className="h-4 w-4" />,
        duration: Infinity,
        position: 'bottom-right',
        cancel: {
          label: 'Got it',
          onClick: () => dismiss(),
        },
      })
    }
  }, [appName, installable, iosA2HS, toastId, triggerInstall, dismiss])

  return null
}
